import React, { useState, useEffect } from 'react';
import { urls } from './config.dev';
import RadiologyResultModal from './RadiologyResultModal';
import { useNavigate } from 'react-router-dom';
import Topbar from './Topbar';

function Radiology() {
  const [pendingRadiologyExams, setPendingRadiologyExams] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clinicDetails, setClinicDetails] = useState({});
  const [token, setToken] = useState('');
  const [totalRadiologyExams, setTotalRadiologyExams] = useState(0);
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');

  const fetchClinicDetails = async (token) => {
    try {
      const response = await fetch(urls.fetchclinicdetails, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        setClinicDetails(data);
      } else {
        throw new Error('Failed to fetch clinic details');
      }
    } catch (error) {
      console.error('Error fetching clinic details:', error);
    }
  };

  const fetchPendingRadiologyExams = async (token) => {
    try {
      const response = await fetch(urls.pendingradiologyexams, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        const processedData = data.map(patient => ({
          ...patient,
          totalRadiologyExams: (patient.radiology_exam.match(/Radiology Exam:/g) || []).length,
        }));
        setPendingRadiologyExams(processedData);
      } else {
        throw new Error('Failed to fetch pending radiology exams data');
      }
    } catch (error) {
      console.error('Error fetching pending radiology exams data:', error);
    }
  };

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');
        setToken(tokenFromUrl);

        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();
          if (securityData.message === 'Session valid') {
            fetchClinicDetails(tokenFromUrl);
            fetchPendingRadiologyExams(tokenFromUrl);
            const intervalId = setInterval(() => {
              fetchPendingRadiologyExams(tokenFromUrl);
            }, 3000);
            return () => clearInterval(intervalId);
          } else if (securityData.error === 'Session expired') {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        } else {
          throw new Error('Failed to perform security check');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  const handleSubmitResults = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
    setTotalRadiologyExams(patient.totalRadiologyExams);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPatient(null);
    setTotalRadiologyExams(0);
  };

  const handleSaveResults = async (contactId, results, event) => {
    event.preventDefault();
    try {
      console.log('Submitting and saving results for patient ID:', contactId);
      console.log('Results:', results);
      // Implement save logic
    } catch (error) {
      console.error('Error submitting results:', error);
    }
  };

  // Inline styles
  const styles = {
    container: {
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      backgroundColor: '#f5f7fa',
      minHeight: '100vh',
      padding: '35px 20px 20px',
      boxSizing: 'border-box',
    },
    header: {
      color: '#2c3e50',
      marginBottom: '30px',
      textAlign: 'center',
      fontSize: '28px',
      marginTop: '35px',
      fontWeight: '600',
    },
    patientCard: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
      padding: '20px',
      marginBottom: '20px',
      display: 'flex',
      flexDirection: 'column',
      height: '500px',
    },
    patientName: {
      fontSize: '22px',
      fontWeight: '600',
      marginBottom: '15px',
      borderBottom: '1px solid #ecf0f1',
      paddingBottom: '10px',
    },
    patientDetail: {
      margin: '8px 0',
      color: '#34495e',
      fontSize: '16px',
    },
    examContainer: {
      flex: '1 1 auto',
      overflow: 'hidden',
      margin: '15px 0',
      display: 'flex',
      flexDirection: 'column',
    },
    examList: {
      paddingLeft: '20px',
      margin: '0',
      overflowY: 'auto',
      flex: '1',
      maxHeight: '300px',
    },
    examItem: {
      marginBottom: '8px',
      wordBreak: 'break-word',
      paddingRight: '10px',
    },
    submitButton: {
      backgroundColor: '#3498db',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '12px 20px',
      fontSize: '16px',
      cursor: 'pointer',
      marginTop: '15px',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '20px',
      marginTop: '20px',
    },
    badge: {
      display: 'inline-block',
      backgroundColor: '#e74c3c',
      color: 'white',
      borderRadius: '12px',
      padding: '3px 10px',
      fontSize: '14px',
      fontWeight: '600',
      marginLeft: '10px',
    },
  };

  return (
    <div style={styles.container}>
      <Topbar token={urlToken} />
      <h1 style={styles.header}>Pending Radiology Exams</h1>
      <div style={styles.gridContainer}>
        {pendingRadiologyExams.map((patient) => (
          <div key={patient.contact_id} style={styles.patientCard}>
            <div style={styles.patientName}>
              {`${patient.first_name} ${patient.last_name}`}
            </div>
            <div style={styles.patientDetail}>Contact ID: {patient.contact_id}</div>
            <div style={styles.patientDetail}>Age: {patient.age}</div>
            <div style={styles.patientDetail}>Sex: {patient.sex}</div>
            <div style={styles.patientDetail}>Exams to be done:</div>
            <div style={styles.examContainer}>
              <ul style={styles.examList}>
                {patient.radiology_exam.split('\n').map((exam, index) => (
                  <li key={index} style={styles.examItem}>{exam}</li>
                ))}
              </ul>
            </div>
            <div style={styles.patientDetail}>
              Total Radiology Exams:
              <span style={styles.badge}>{patient.totalRadiologyExams}</span>
            </div>
            <button style={styles.submitButton} onClick={() => handleSubmitResults(patient)}>
              Submit Results
            </button>
          </div>
        ))}
      </div>

      {showModal && selectedPatient && (
        <RadiologyResultModal
          patient={selectedPatient}
          clinicDetails={clinicDetails}
          token={token}
          totalRadiologyExams={totalRadiologyExams}
          examsToBeDone={selectedPatient.radiology_exam.split('\n')}
          onClose={handleCloseModal}
          onSubmit={handleSaveResults}
        />
      )}
    </div>
  );
}

export default Radiology;
